import {
	AppBar,
	Avatar,
	Badge,
	Button,
	Checkbox,
	createTheme,
	Dialog,
	DialogContent,
	FormControlLabel,
	Grid,
	IconButton,
	Link,
	Paper,
	Slide,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Toolbar,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material"
import { forwardRef, useContext, useEffect, useState } from "react"
import { AdminContext } from "../views/Admin"
import CloseIcon from "@mui/icons-material/Close"
import {
	adminExecuteCreditTransactionV2,
	adminExecuteJ4TransactionV2,
	adminGetUserProfileV2,
	adminUpdateUserProfileV2,
} from "../util/firebase"
import { firebaseTimeStampToDate, formatLicenseNumber, statuses } from "../util/helpers"
import RemoveCircleTwoToneIcon from "@mui/icons-material/RemoveCircleTwoTone"
import HelpIcon from "@mui/icons-material/Help"
import { green } from "@mui/material/colors"

const Transition = forwardRef(function Transition(props, ref) {
	return <Slide direction="right" ref={ref} {...props} />
})

const mdTheme = createTheme({
	palette: {
		primary: {
			main: "#132242",
		},
		secondary: {
			main: "#DF0A0A",
		},
		white: {
			main: "white",
		},
	},
})

const UserProfileDialog = () => {
	const adminContext = useContext(AdminContext)
	const userDialogOpen = adminContext.userDialogOpen
	const setUserDialogOpen = adminContext.setUserDialogOpen
	const userId = adminContext.userId

	const handleOpenSnackbar = adminContext.handleOpenSnackbar

	const handlePostOptionOpen = adminContext.handlePostOptionOpen

	const matches = useMediaQuery(mdTheme.breakpoints.up("md"))
	const [userProfile, setUserProfile] = useState(null)
	const [editMode, setEditMode] = useState(false)
	const [privateMessage, setPrivateMessage] = useState("")

	const [amount, setAmount] = useState("")
	const [creditPostId, setCreditPostId] = useState("")
	const [comment, setComment] = useState("")
	const [amountError, setAmountError] = useState(false)

	const handleJ4Transaction = async () => {
		if (!isNaN(parseInt(amount))) {
			try {
				const response = await adminExecuteJ4TransactionV2({
					amount: parseInt(amount),
					postId: creditPostId,
					userId: userId,
					comment: comment,
				})
				if (response.data.status === 0 && response.data.result) {
					handleOpenSnackbar("משתמש חויב בהצלחה!", "success")
					setAmount("")
					setCreditPostId("")
					setComment("")
					setAmountError(false)
				} else {
					handleOpenSnackbar("תקלה בחיוב משתמש", "error")
				}
			} catch (e) {
				console.log(e)
			}
		} else {
			setAmountError(true)
		}
	}

	const handleCreditTransaction = async () => {
		if (!isNaN(parseInt(amount))) {
			try {
				const response = await adminExecuteCreditTransactionV2({
					amount: parseInt(amount),
					postId: creditPostId,
					userId: userId,
					comment: comment,
				})
				if (response.data.status === 0 && response.data.result) {
					handleOpenSnackbar("משתמש זוכה בהצלחה!", "success")
					setAmount("")
					setCreditPostId("")
					setComment("")
					setAmountError(false)
				} else {
					handleOpenSnackbar("תקלה בזיכוי משתמש", "error")
				}
			} catch (e) {
				console.log(e)
			}
		} else {
			setAmountError(true)
		}
	}

	const _getUserProfile = async () => {
		const response = await adminGetUserProfileV2({ userId: userId })
		setUserProfile(response.data)
	}

	useEffect(() => {
		if (userId && userDialogOpen) _getUserProfile()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId, userDialogOpen])

	const handleFieldChange = (e, field) => {
		setUserProfile((prevState) => ({
			...prevState,
			[field]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
		}))
	}

	const handleUpdateUserProfile = async () => {
		try {
			await adminUpdateUserProfileV2(userProfile)
			handleOpenSnackbar("משתמש עודכן בהצלחה!", "success")
			setEditMode(false)
		} catch (e) {
			console.log(e)
			handleOpenSnackbar("תקלה בעידכון משתמש", "error")
		}
	}

	const handlePrivateMessage = async () => {
		try {
			const response = await adminUpdateUserProfileV2({ email: userId, privateMessage: privateMessage })
			if (response?.data?.status === 0) {
				//await refreshPost(post.id, postStatus)
				handleOpenSnackbar("נשלחה הודעה ללקוח בהצלחה", "success")
			}
		} catch (e) {
			console.log(e)
			handleOpenSnackbar("תקלה בשליחת הודעה ללקוח", "error")
		}
	}

	return (
		<Dialog
			fullScreen={!matches}
			open={userDialogOpen}
			onClose={() => setUserDialogOpen(false)}
			TransitionComponent={Transition}
			PaperProps={{
				style: {
					backgroundColor: "#7C879F",
					maxHeight: "715px",
				},
			}}
			maxWidth={"sm"}
		>
			<AppBar sx={{ position: "relative" }}>
				<Toolbar>
					<IconButton edge="start" color="inherit" onClick={() => setUserDialogOpen(false)} aria-label="close">
						<CloseIcon />
					</IconButton>
					<Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
						{userProfile?.displayName}
					</Typography>
				</Toolbar>
			</AppBar>
			<DialogContent dividers>
				<Paper sx={{ p: 2 }}>
					<Typography sx={{ mb: 2 }} variant="h5">
						פרטי משתמש
					</Typography>
					<div style={{ margin: "0px 16px 0px 16px" }}>
						<Grid container spacing={2} /* alignItems="center" */ direction={matches ? "row" : "column"}>
							<Grid item xs={12} md={12}>
								<div style={{ display: "flex", justifyContent: "center" }}>
									<Badge
										invisible={!editMode || !userProfile?.photoURL}
										overlap="circular"
										anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
										badgeContent={
											<IconButton
												sx={{ backgroundColor: "white" }}
												onClick={() => handleFieldChange({ target: { value: null } }, "photoURL")}
											>
												<RemoveCircleTwoToneIcon color="secondary" />
											</IconButton>
										}
									>
										<Avatar src={userProfile?.photoURL} sx={{ width: 56, height: 56 }} />
									</Badge>
								</div>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									label={"שם"}
									value={userProfile?.displayName ?? ""}
									disabled={!editMode}
									onChange={(e) => handleFieldChange(e, "displayName")}
									fullWidth
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									label={"טלפון"}
									value={userProfile?.phoneNumber ?? ""}
									disabled={!editMode}
									onChange={(e) => handleFieldChange(e, "phoneNumber")}
									fullWidth
									sx={{ ".MuiInputBase-input": { direction: "rtl" } }}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									label={"ת. זהות"}
									value={userProfile?.idNumber ?? ""}
									disabled={!editMode}
									onChange={(e) => handleFieldChange(e, "idNumber")}
									fullWidth
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									label={"כתובת"}
									value={userProfile?.address ?? ""}
									disabled={!editMode}
									onChange={(e) => handleFieldChange(e, "address")}
									fullWidth
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField label={'דוא"ל'} value={userProfile?.email ?? ""} disabled fullWidth />
							</Grid>
							<Grid item xs={12} md={6}>
								<FormControlLabel
									disabled={!editMode}
									control={
										<Checkbox
											checked={userProfile?.emailVerified ?? false}
											onChange={(e) => handleFieldChange(e, "emailVerified")}
										/>
									}
									label="מייל מאומת?"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<FormControlLabel
									disabled={!editMode || !userProfile?.hasCreditInfo}
									control={
										<Checkbox
											checked={userProfile?.hasCreditInfo ?? false}
											onChange={(e) => handleFieldChange(e, "hasCreditInfo")}
										/>
									}
									label="קיימים פרטי אשראי?"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<FormControlLabel
									disabled={!editMode}
									control={
										<Checkbox
											checked={userProfile?.allowExtraEmails ?? false}
											onChange={(e) => handleFieldChange(e, "allowExtraEmails")}
										/>
									}
									label="אישור דואר שיווקי"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Typography sx={{ textDecoration: "underline" }}>נוצר ב:</Typography>
								<Typography>{new Date(userProfile?.createDateTime)?.toLocaleString("he")}</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<Typography sx={{ textDecoration: "underline" }}>כניסה אחרונה:</Typography>
								<Typography>{new Date(userProfile?.lastSignInDateTime)?.toLocaleString("he")}</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<Button
									fullWidth
									variant="outlined"
									/* sx={{ ml: 2, mr: 2 }} */ onClick={() => {
										setEditMode(!editMode)
										_getUserProfile()
									}}
								>
									{editMode ? "ביטול" : "עריכה"}
								</Button>
							</Grid>
							<Grid item xs={12} md={6}>
								{editMode && (
									<Button
										fullWidth
										variant="contained"
										sx={{
											backgroundColor: mdTheme.palette.secondary.main,
											"&:hover": { backgroundColor: mdTheme.palette.secondary.main },
										}}
										onClick={handleUpdateUserProfile}
									>
										שמירה
									</Button>
								)}
							</Grid>
						</Grid>
					</div>
				</Paper>
				<Paper sx={{ p: 2, mt: 2 }}>
					<Typography sx={{ mb: 2 }} variant="h5">
						שליחת הודעה ללקוח
					</Typography>
					<TextField
						fullWidth
						label="הערה ללקוח"
						multiline
						rows={5}
						sx={
							{
								/* m: matches ? 0 : 2 */
								/* , height: "100%" */
							}
						}
						value={privateMessage}
						onChange={(e) => setPrivateMessage(e.target.value)}
					/>
					<Button
						sx={{
							m: 2,
							width: matches ? "120px" : "auto",
							backgroundColor: mdTheme.palette.secondary.main,
							"&:hover": { backgroundColor: mdTheme.palette.secondary.main },
						}}
						variant="contained"
						onClick={handlePrivateMessage}
					>
						שליחה
					</Button>
				</Paper>
				<Paper sx={{ p: 2, mt: 2 }}>
					<Typography sx={{ mb: 2 }} variant="h5">
						חיוב/זיכוי לקוח
					</Typography>
					<Grid container spacing={2} /* alignItems="center" */ direction={matches ? "row" : "column"}>
						<Grid item xs={12} md={6}>
							<TextField
								label={"סכום"}
								value={amount}
								disabled={!userProfile?.hasCreditInfo}
								onChange={(e) => setAmount(e.target.value)}
								fullWidth
								error={amountError}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								label={"מזהה מודעה"}
								value={creditPostId}
								disabled={!userProfile?.hasCreditInfo}
								onChange={(e) => setCreditPostId(e.target.value)}
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} md={12}>
							<TextField
								label={"הערה"}
								value={comment}
								disabled={!userProfile?.hasCreditInfo}
								onChange={(e) => setComment(e.target.value)}
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} md={12} sx={{ textAlign: "center" }}>
							<Button
								sx={{
									m: 2,
									width: matches ? "120px" : "auto",
									backgroundColor: mdTheme.palette.secondary.main,
									"&:hover": { backgroundColor: mdTheme.palette.secondary.main },
								}}
								variant="contained"
								onClick={handleJ4Transaction}
								disabled={!userProfile?.hasCreditInfo}
							>
								חיוב
							</Button>
							<Button
								sx={{
									m: 2,
									width: matches ? "120px" : "auto",
									backgroundColor: green[500],
									"&:hover": { backgroundColor: green[500] },
								}}
								variant="contained"
								onClick={handleCreditTransaction}
								disabled={!userProfile?.hasCreditInfo}
							>
								זיכוי
							</Button>
						</Grid>
					</Grid>
				</Paper>
				<Paper sx={{ p: 2, mt: 2 }}>
					<Typography sx={{ mb: 2 }} variant="h5">
						חיובים
					</Typography>
					{userProfile?.transactions?.length > 0 && (
						<Table
							sx={{
								tableLayout: "fixed",
								"th:nth-of-type(2), td:nth-of-type(2)": { width: "50px" },
								"th:nth-of-type(1), td:nth-of-type(1)": { width: "110px" },
							}}
						>
							<TableHead>
								<TableRow>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										מודעה
									</TableCell>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										סוג
									</TableCell>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										סכום
									</TableCell>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										תאריך
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{userProfile?.transactions?.map((transaction, index) => (
									<TableRow key={transaction.tranId}>
										<TableCell>
											<Link
												target="_blank"
												sx={{ cursor: "pointer" }}
												onClick={() => handlePostOptionOpen(transaction.postId, transaction.status)}
											>
												{formatLicenseNumber(transaction.licenseNumber)}
											</Link>
										</TableCell>
										<TableCell>{transaction.type}</TableCell>
										<TableCell>
											{"\u20aa" + new Intl.NumberFormat().format(parseInt(transaction.amount / 100))}
											{transaction.comment && (
												<Tooltip title={transaction.comment}>
													<HelpIcon />
												</Tooltip>
											)}
										</TableCell>
										<TableCell>{firebaseTimeStampToDate(transaction.dateTime)?.toLocaleString("he")}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
				<Paper sx={{ p: 2, mt: 2 }}>
					<Typography sx={{ mb: 2 }} variant="h5">
						מודעות
					</Typography>
					{userProfile?.posts?.length > 0 && (
						<Table
							sx={{
								tableLayout: "fixed",
								"th:nth-of-type(2), td:nth-of-type(2)": { width: "80px" },
								"th:nth-of-type(1), td:nth-of-type(1)": { width: "110px" },
							}}
						>
							<TableHead>
								<TableRow>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										מודעה
									</TableCell>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										סטטוס
									</TableCell>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										נוצרה
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{userProfile?.posts?.map((post, index) => (
									<TableRow key={post.postId}>
										<TableCell>
											<Link
												target="_blank"
												sx={{ cursor: "pointer" }}
												onClick={() => handlePostOptionOpen(post.postId, post.status)}
											>
												{formatLicenseNumber(post.licenseNumber)}
											</Link>
										</TableCell>
										<TableCell>{statuses[post.status]}</TableCell>
										<TableCell>{firebaseTimeStampToDate(post.createDateTime)?.toLocaleString("he")}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
				<Paper sx={{ p: 2, mt: 2 }}>
					<Typography sx={{ mb: 2 }} variant="h5">
						הצעות
					</Typography>
					{userProfile?.bids?.length > 0 && (
						<Table
							sx={{
								tableLayout: "fixed",
								"th:nth-of-type(2), td:nth-of-type(2)": { width: "80px" },
								"th:nth-of-type(1), td:nth-of-type(1)": { width: "110px" },
							}}
						>
							<TableHead>
								<TableRow>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										מודעה
									</TableCell>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										הצעה
									</TableCell>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										ניתנה
									</TableCell>
									{/* <TableCell variant="head" sx={{ fontWeight: 600 }}>
										צפיה
									</TableCell>
									<TableCell variant="head" sx={{ fontWeight: 600 }}>
										ציבורי?
									</TableCell> */}
								</TableRow>
							</TableHead>
							<TableBody>
								{userProfile?.bids?.map((bid, index) => (
									<TableRow
										key={bid.postId}
										/* sx={{ backgroundColor: filterArray.indexOf(doc.fileName) > -1 ? "pink" : "#BFDDFF" }} */
									>
										<TableCell>
											<Link
												target="_blank"
												sx={{ cursor: "pointer" }}
												onClick={() => handlePostOptionOpen(bid.postId, bid.status)}
											>
												{formatLicenseNumber(bid.licenseNumber)}
											</Link>
										</TableCell>
										<TableCell>{"\u20aa" + new Intl.NumberFormat().format(parseInt(bid.bid))}</TableCell>
										<TableCell>{firebaseTimeStampToDate(bid.timeStamp)?.toLocaleString("he")}</TableCell>
										{/* <TableCell>
											<Link sx={{ cursor: "pointer" }} onClick={() => openDoc(doc.fileName)}>
												צפיה
											</Link>
										</TableCell>
										<TableCell>
											<Checkbox
												size="small"
												checked={filterArray.indexOf(doc.fileName) > -1}
												onChange={(e) => handleTogglePublicDocument(e.target.checked, index)}
											/>
										</TableCell> */}
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
			</DialogContent>
		</Dialog>
	)
}

export default UserProfileDialog
